import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useEnquiry } from "../enquiry-context";
import { PRODUCT_OPTIONS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldCls =
  "w-full rounded-lg border border-slate-700 bg-[#0D1530] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 transition-all";

export default function EnquiryModal({ open, product, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [selectedProduct, setSelectedProduct] = useState("General Enquiry");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedProduct(product || "General Enquiry");
      setDone(false);
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, product]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, product: selectedProduct }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send enquiry");
      setDone(true);
      toast.success("Enquiry sent! We will get back to you shortly.");
      setTimeout(() => {
        onClose();
        setForm({ name: "", phone: "", email: "", message: "" });
      }, 1600);
    } catch (err) {
      toast.error(err.message || "Could not send enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#14171C]/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            data-testid="query-modal-dialog"
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-2xl border border-slate-700/80 bg-[#0F172A] p-6 sm:p-8 shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="query-modal-close-button"
              aria-label="Close enquiry form"
              className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition-colors"
            >
              <X size={20} />
            </button>

            {done ? (
              <div className="py-12 text-center" data-testid="query-form-success">
                <CheckCircle2 size={56} className="mx-auto text-amber-500" />
                <h3 className="mt-5 font-display text-3xl font-bold uppercase text-slate-100">
                  Enquiry Received
                </h3>
                <p className="mt-2 text-sm text-slate-400">
                  Thank you — our team will contact you shortly.
                </p>
              </div>
            ) : (
              <>
                <p className="eyebrow">Send Enquiry</p>
                <h3 className="mt-2 font-display text-3xl font-bold uppercase leading-none text-slate-100">
                  Tell us what to cast
                </h3>
                <p className="mt-2 text-xs font-tech uppercase tracking-wider text-amber-400/90">
                  {selectedProduct}
                </p>

                <form className="mt-6 space-y-3.5" onSubmit={submit} data-testid="query-form">
                  <input
                    data-testid="query-form-input-name"
                    className={fieldCls}
                    placeholder="Full name *"
                    value={form.name}
                    onChange={set("name")}
                    required
                    minLength={2}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <input
                      data-testid="query-form-input-phone"
                      className={fieldCls}
                      placeholder="Phone number *"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      required
                    />
                    <input
                      data-testid="query-form-input-email"
                      className={fieldCls}
                      placeholder="Email address *"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <select
                    data-testid="query-form-input-product"
                    className={`${fieldCls} appearance-none`}
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                  >
                    {PRODUCT_OPTIONS.map((p) => (
                      <option key={p} value={p} className="bg-[#0D1530]">
                        {p}
                      </option>
                    ))}
                  </select>
                  <textarea
                    data-testid="query-form-input-message"
                    className={`${fieldCls} min-h-[96px] resize-y`}
                    placeholder="Quantity, drawings, specifications or any other requirement…"
                    value={form.message}
                    onChange={set("message")}
                  />
                  <button
                    type="submit"
                    data-testid="query-form-submit-button"
                    disabled={sending}
                    className="w-full rounded-lg bg-amber-500 py-3.5 text-sm font-bold uppercase tracking-widest text-[#080C14] hover:bg-amber-400 hover:shadow-[0_0_32px_rgba(245,158,11,0.35)] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      "Submit Enquiry"
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
